/**
 * Typen und reine Hilfen der Kälteseite (TH-1, 17.10.2026).
 *
 * Die Route `GET /api/projects/{id}/kaelte` liefert seit TH-1 zusätzlich den
 * jüngsten beendeten Bilanzlauf (`lauf`), die Annahmenliste (`annahmen`) und
 * den Vermerk zu den festen Zeilen (`festeBilanzStand`). Die Grundtypen
 * stehen unverändert in `api.ts`; die Erweiterung steht hier, damit die
 * Sammeldatei nur durch Anhängen wächst (TX-STANDARD S0.3).
 */

import type { Kaelteseite } from '../../api';
import type { Zustand } from '../../muster';

export interface KaelteLaufzeile {
  /** erzeugung | rueckkuehlung | speicher_entzug | speicher_regeneration */
  bilanzart: string;
  /** A | B1 | B2 | - (eine Lesart, keine ist „richtig“) */
  variante: string;
  bezeichnung: string;
  erforderlichKw: number | null;
  installiertKw: number | null;
  reserveKw: number | null;
  deckungProzent: number | null;
  kapazitaetKwh: number | null;
  dauerH: number | null;
  /** gedeckt | unterdeckt | hinweis */
  stand: string;
  rechenweg: string;
  quellen: string | null;
  reihenfolge: number;
}

export interface KaelteLaufbefund {
  thema: string;
  /** offen | geklaert */
  stand: string;
  titel: string;
  werte: string;
  frage: string | null;
  reihenfolge: number;
}

export interface KaelteBilanzlauf {
  id: string;
  gestartetAm: string;
  beendetAm: string | null;
  kuehllastLaufnummer: number | null;
  kuehllastAm: string | null;
  kuehllastSpitzeKw: number | null;
  summeRaumspitzenKw: number | null;
  gleichzeitigkeit: number | null;
  vollstaendigkeitProzent: number | null;
  kuehllastVorlaeufig: boolean;
  aussentemperaturMaxC: number | null;
  datenstand: string;
  datenhash: string | null;
  hinweise: string[];
  zeilen: KaelteLaufzeile[];
  befunde: KaelteLaufbefund[];
}

export interface KaelteAnnahme {
  parameter: string;
  wert: number | null;
  textwert: string | null;
  einheit: string | null;
  herkunft: string;
  quelle: string | null;
  bestaetigt: boolean;
}

/** Der heute maßgebende Kühllast-Lauf (Huelle/Auslegungslauf). */
export interface KaelteKuehllastwahl {
  laufnummer: number;
  /** gekennzeichnet | juengster_fertiger */
  grundlage: string;
  hinweis: string | null;
}

export interface KaelteseiteMitLauf extends Kaelteseite {
  lauf: KaelteBilanzlauf | null;
  annahmen: KaelteAnnahme[];
  festeBilanzStand: string;
  massgebendeKuehllast: KaelteKuehllastwahl | null;
}

/**
 * Ob der Bilanzlauf auf einem anderen Kühllast-Lauf steht als dem heute
 * maßgebenden (TX-STANDARD S1.5, Rechenstand „veraltet“).
 */
export function bilanzVeraltet(
  lauf: KaelteBilanzlauf | null, massgebend: KaelteKuehllastwahl | null,
): boolean {
  if (!lauf || !massgebend) return false;
  return lauf.kuehllastLaufnummer !== massgebend.laufnummer;
}

/** Die Bilanzarten in der Sprache der Seite. */
export const BILANZART: Record<string, string> = {
  erzeugung: 'Erzeugung',
  rueckkuehlung: 'Rückkühlung',
  speicher_entzug: 'Speicher Entzug',
  speicher_regeneration: 'Speicher Regeneration',
  speicher: 'Speicher',
};

/** Fachstatus einer Bilanzzeile nach TX-STANDARD S1.5 (Prüfergebnis). */
export function zustandBilanz(stand: string): { zustand: Zustand; text: string } {
  if (stand === 'gedeckt') return { zustand: 'gut', text: 'gedeckt' };
  if (stand === 'unterdeckt') return { zustand: 'fehler', text: 'unterdeckt' };
  return { zustand: 'info', text: 'Hinweis' };
}

/** Fachstatus eines Befunds: offen = warnung, geklärt = gut (Koordination, S1.5). */
export function zustandBefund(stand: string): { zustand: Zustand; text: string } {
  return stand === 'offen'
    ? { zustand: 'warnung', text: 'offen' }
    : { zustand: 'gut', text: 'geklärt' };
}

/**
 * Herkunft eines Werts nach S1.5: Modell/Datenblatt/von Hand/Quelle neutral,
 * Vorgabe/Annahme/Schätzung/gerechnet ohne Herstellerpunkt warnung.
 */
export function zustandHerkunft(herkunft: string): { zustand: Zustand; text: string } {
  switch (herkunft) {
    case 'datenblatt': return { zustand: 'neutral', text: 'Datenblatt' };
    case 'modell': return { zustand: 'neutral', text: 'Modell' };
    case 'hand': return { zustand: 'neutral', text: 'von Hand' };
    case 'quelle': return { zustand: 'neutral', text: 'Quelle' };
    case 'fachplaner': return { zustand: 'neutral', text: 'Fachplaner' };
    case 'vorgabe': return { zustand: 'warnung', text: 'Vorgabe' };
    case 'annahme': return { zustand: 'warnung', text: 'Annahme' };
    case 'berechnet': return { zustand: 'warnung', text: 'gerechnet' };
    default: return { zustand: 'warnung', text: herkunft };
  }
}

/** Datum und Uhrzeit in der Form des Rechenstands (S1.2), UTC. */
export function zeitpunkt(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const zwei = (n: number) => String(n).padStart(2, '0');
  return `${zwei(d.getUTCDate())}.${zwei(d.getUTCMonth() + 1)}.${d.getUTCFullYear()} `
    + `${zwei(d.getUTCHours())}:${zwei(d.getUTCMinutes())} UTC`;
}

/**
 * Der Rechenstand der Seite (S1.2): „Lauf ‹Kurz-ID› · gerechnet ‹…› ·
 * Kühllast-Lauf ‹Nr› vom ‹…›“ – oder „noch nicht gerechnet“.
 */
export function rechenstand(lauf: KaelteBilanzlauf | null): string {
  if (!lauf) return 'noch nicht gerechnet';
  const teile = [
    `Lauf ${lauf.id.slice(0, 8)}`,
    `gerechnet ${zeitpunkt(lauf.beendetAm ?? lauf.gestartetAm)}`,
  ];
  if (lauf.kuehllastLaufnummer !== null) {
    teile.push(`Kühllast-Lauf ${lauf.kuehllastLaufnummer} vom ${zeitpunkt(lauf.kuehllastAm)}`);
  }
  return teile.join(' · ');
}

/** Die Zahl der Annahmen, die nicht bestätigt sind. */
export function unbestaetigt(annahmen: readonly KaelteAnnahme[]): number {
  return annahmen.filter((a) => !a.bestaetigt).length;
}
